import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loginUser, secureLoginUser, clearError } from '../../store/slices/authSlice';
import { 
  Box, 
  Button, 
  Checkbox, 
  FormControlLabel, 
  TextField, 
  Typography, 
  Alert, 
  CircularProgress, 
  Switch, 
  FormControl,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import PageMeta from '../../components/PageMeta';

const MAX_ATTEMPTS = 5;
const LOCKOUT_MINUTES = 5;

const getLockoutData = () => {
  const data = localStorage.getItem('loginLockout');
  return data ? JSON.parse(data) : { count: 0, until: null };
};

const setLockoutData = (data) => {
  localStorage.setItem('loginLockout', JSON.stringify(data));
};

const Login = () => {
  const { t } = useTranslation(['auth', 'common']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, isLoggedIn } = useSelector((state) => state.auth);
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [useSecureLogin, setUseSecureLogin] = useState(true); // Default to secure login
  const [lockout, setLockout] = useState(getLockoutData());
  const [now, setNow] = useState(Date.now());

  // Add input validation/touched state and getInputColor helper
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  // Helper to determine input color
  const getInputColor = (value, touched, isValid = null) => {
    if (!touched) return 'default';
    if (isValid !== null) {
      return isValid ? 'success' : 'error';
    }
    return value.trim() ? 'success' : 'error';
  };

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    setLockout(getLockoutData());
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/admin/dashboard');
    }
  }, [isLoggedIn, navigate]);

  const isLockedOut = lockout.until && now < lockout.until;
  const lockoutSeconds = lockout.until ? Math.max(0, Math.floor((lockout.until - now) / 1000)) : 0;

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (isLockedOut) {
      return;
    }

    try {
      if (useSecureLogin) {
        await dispatch(secureLoginUser({ username, password })).unwrap();
      } else {
        await dispatch(loginUser({ username, password })).unwrap();
      }
      setLockoutData({ count: 0, until: null });
      setLockout({ count: 0, until: null });
    } catch (error) {
      // Only increment lockout for wrong password, not for non-existent user or decryption errors
      const errorMsg = error?.message || '';
      const wrongPassword = errorMsg.toLowerCase().includes('invalid password') || errorMsg.toLowerCase().includes('incorrect password');
      if (wrongPassword) {
        const newCount = lockout.count + 1;
        let until = null;
        if (newCount >= MAX_ATTEMPTS) {
          until = Date.now() + LOCKOUT_MINUTES * 60 * 1000;
        }
        const newLockout = { count: newCount, until };
        setLockoutData(newLockout);
        setLockout(newLockout);
      }
      // For other errors, do not increment lockout
    }
  };

  // Update handleInputChange to set touched state
  const handleInputChange = (setter, setTouched) => (e) => {
    setter(e.target.value);
    setTouched(true);
    if (error) {
      dispatch(clearError());
    }
  };

  const handleCheckboxChange = (e) => {
    setRememberMe(e.target.checked);
  };

  const handleSecureLoginToggle = (e) => {
    setUseSecureLogin(e.target.checked);
    if (error) {
      dispatch(clearError());
    }
  };

  const handlePasswordToggle = () => {
    setShowPassword(!showPassword);
  };

  const canLogin = username.trim() && password.trim() && !isLockedOut && !loading;

  return (
    <>
      <PageMeta
        title={t('login.title')}
        description={t('auth:login.metaDescription', {
          defaultValue: 'Sign in to the Checkstall Import admin dashboard to manage orders, users and settings.'
        })}
      />
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 8, p: 3, border: '1px solid #eee', borderRadius: 2, boxShadow: 1 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: '#DC143C' }}>{t('login.title')}</Typography>
      <form onSubmit={handleLogin}>
        <TextField
          label={t('login.username')}
          value={username}
          onChange={handleInputChange(setUsername, setUsernameTouched)}
          fullWidth
          margin="normal"
          autoComplete="username"
          disabled={loading}
          color={getInputColor(username, usernameTouched)}
          focused={usernameTouched}
        />
        <TextField
          label={t('login.password')}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={handleInputChange(setPassword, setPasswordTouched)}
          fullWidth
          margin="normal"
          autoComplete="current-password"
          disabled={loading}
          color={getInputColor(password, passwordTouched)}
          focused={passwordTouched}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handlePasswordToggle}
                  edge="end"
                  disabled={loading}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={<Checkbox checked={rememberMe} onChange={handleCheckboxChange} disabled={loading} />}
          label={t('login.rememberMe')}
        />
        <FormControlLabel
          control={
            <Switch
              checked={useSecureLogin}
              onChange={handleSecureLoginToggle}
              disabled={loading}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body2" color="primary" fontWeight="bold">
                {t('login.secureLogin')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {useSecureLogin ? t('login.secureLogin') : 'Standard login'}
              </Typography>
            </Box>
          }
          sx={{ mt: 1, mb: 1 }}
        />
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {isLockedOut && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('login.errors.accountLocked', { minutes: Math.ceil(lockoutSeconds / 60) })}
          </Alert>
        )}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={!canLogin}
          sx={{ opacity: canLogin ? 1 : 0.5, mt: 1, mb: 2 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t('login.loginButton')}
        </Button>
      </form>
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('login.forgotPassword')}
        </Typography>
        <Link 
          to="/admin/reset-password" 
          style={{ 
            color: '#DC143C', 
            textDecoration: 'none',
            fontWeight: 'bold'
          }}
        >
          {t('login.forgotPassword')}
        </Link>
      </Box>
      {useSecureLogin && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Secure Mode:</strong> Your password is encrypted client-side before transmission, 
            protecting against local system compromise.
          </Typography>
        </Alert>
      )}
    </Box>
    </>
  );
};

export default Login; 
